// The two small controls the board is built out of.
//
// Both are deliberately tiny and deliberately not generic: a level indicator and a
// show/hide toggle are the only two things a board row needs that the rest of the
// app doesn't already have.

import { MAX_PRIORITY, type Visibility } from '../lib/model'
import { showsToClient } from '../lib/model'

/**
 * The client's priority, as bars rather than stars.
 *
 * Stars read as a rating of quality; bars read as a level, which is what this is.
 * Three points, not five: a five-point scale is one nobody applies consistently,
 * and the colour does the reading anyway — green at one, amber at two, red at
 * three, a flat track at zero.
 *
 * **One target, not three.** The editable version was three separate buttons, so
 * setting a level meant hitting the right 14px column — and the gaps that made
 * those targets separable also made the indicator read as three loose marks rather
 * than one meter. It is one button now: a click raises the level and loops back to
 * unset after the top, which is also how you clear it.
 */
export function Priority({
  value,
  onSet,
}: {
  value: number
  onSet?: ((v: number) => void) | undefined
}) {
  const level = Math.min(Math.max(Math.round(value) || 0, 0), MAX_PRIORITY)
  // Brass, not `--warn`. Warn is a brown-orange that sits close enough to crit's
  // red that two 3px bars could not be told apart at a glance — and the whole point
  // of three levels is telling them apart at a glance. Green, gold, red.
  const colour = level === 1 ? 'var(--good)' : level === 2 ? 'var(--brass)' : 'var(--crit)'
  const label = level === 0 ? 'No priority' : `Priority ${level} of ${MAX_PRIORITY}`
  const bars = (
    <span className="inline-flex items-end gap-[1.5px] align-middle">
      {[1, 2, 3].map((n) => (
        <span
          key={n}
          className="block w-[3px] rounded-[1px]"
          style={{
            height: 12,
            background: n <= level ? colour : 'color-mix(in srgb, var(--fg) 14%, transparent)',
          }}
        />
      ))}
    </span>
  )

  if (!onSet) return <span title={label}>{bars}</span>

  return (
    // Negative margin keeps the meter exactly where it sits while giving the click
    // a real target: what you look at and what you hit are different sizes.
    <button
      type="button"
      onClick={() => onSet((level + 1) % (MAX_PRIORITY + 1))}
      title={`${label} · click to raise`}
      aria-label={`${label}. Click to raise.`}
      className="rounded-inline -m-1.5 flex h-7 items-center p-1.5 hover:bg-[var(--hover)]"
    >
      {bars}
    </button>
  )
}

/**
 * Whether a row reaches the client's page.
 *
 * The one extra act the team performs on a board, and the reason the client
 * view costs nothing to maintain: there is no publish step, only this.
 */
export function VisibilityEye({
  value,
  onToggle,
}: {
  value: Visibility
  onToggle?: (() => void) | undefined
}) {
  const on = showsToClient(value)
  const label = on
    ? value === 'acknowledgeable'
      ? 'Shown to the client, and nameable elsewhere'
      : 'Shown to the client'
    : 'Internal only'
  const glyph = (
    <span
      aria-hidden
      style={{
        color: on ? 'var(--ember)' : 'color-mix(in srgb, var(--fg) 22%, transparent)',
        fontSize: 'var(--text-body)',
        lineHeight: 1,
      }}
    >
      {on ? '◉' : '○'}
    </span>
  )
  if (!onToggle) return <span title={label}>{glyph}</span>
  return (
    // -m-2 p-2 keeps the glyph exactly where it was and gives it a 32px target:
    // the thing you look at and the thing you hit are different sizes on a phone.
    <button
      type="button"
      onClick={onToggle}
      title={label}
      aria-label={label}
      className="-m-2 flex h-8 w-8 items-center justify-center p-2"
    >
      {glyph}
    </button>
  )
}
